import { ElementRef, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, catchError, Observable, Subscription, tap, throwError } from 'rxjs';
import { MatExpansionPanel } from '@angular/material/expansion';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SlideAction, TutorialPosition, TutorialSection, TutorialSlide, UserTutorialStatus, UserTutorialStatusType } from './tutorial.models';
import { UserService } from '../services/user.service';
import { GroupService } from '../services/group.service';
import { HospitalService } from '../services/hospital.service';
import { MedicationService } from '../services/medication.service';
import { ScheduleService } from '../services/schedule.service';
import { AppointmentTypeService } from '../services/appointment-type.service';
import { ConsentFormsService } from '../services/consent-forms.service';
import { ColleagueService } from '../services/colleague.service';
import { CodingBundleService } from '../services/coding-bundle.service';
import { NoteTypeService } from '../services/note-type.service';
import { evaluateHideConditions } from './tutorial-conditions';
import { PROFILE_SETTINGS_SLIDES } from './slides/settings/profile-settings-slides';
import { NOTE_SETTINGS_SLIDES } from './slides/settings/note-settings-slides';
import { SCHEDULE_SETTINGS_SLIDES } from './slides/settings/schedule-settings-slides';
import { CONSENT_SETTINGS_SLIDES } from './slides/settings/consent-settings-slides';
import { CODING_SETTINGS_SLIDES } from './slides/settings/coding-settings-slides';
import { NETWORK_SETTINGS_SLIDES } from './slides/settings/network-settings-slides';
import { SCHEDULE_SLIDE_NAV } from './slides/sidenav/schedule-slides';
import { PATIENT_SLIDE_NAV } from './slides/sidenav/patient-slides';
import { Router, NavigationEnd } from '@angular/router';
import { GETTING_STARTED } from './slides/on-first-login/getting-started-slides';
import { CLOSING_SLIDE } from './slides/settings/closing-slide';
import { OLD_USER_LOGIN } from './slides/on-first-login/old-user-login-slids';
import { DOCTER_OPTION_SLIDES } from './slides/on-first-login/doctor-slide-options';
import { DOCTOR_ONLY_PROFILE_SETTINGS_SLIDES } from './slides/settings/doctor-only-profile-settings-slides';
import * as moment from 'moment';
import { FeedbackService } from '../services/feedback.service';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class TutorialService {
    private currentSlideIndex: number = 0;
    public isTutorialVisible = false; // Track the visibility of the tutorial card
    hiddenSlides: string[] = []; // Initialize as an empty array
    //INITIALISE AN INTERGER VARIABLE

    private currentSlideSubject = new BehaviorSubject<TutorialSlide | null>(null);
    currentSlide$: Observable<TutorialSlide | null> = this.currentSlideSubject.asObservable();

    private tabChangeSubject = new BehaviorSubject<number | null>(null);
    tabChange$: Observable<number | null> = this.tabChangeSubject.asObservable();

    public forceZIndex = new BehaviorSubject<number | null>(null)

    private expansionTargets = new Map<string, MatExpansionPanel>();
    private highlightTargets = new Map<string, ElementRef<HTMLElement>>();
    private formTargets = new Map<string, ElementRef<HTMLElement>>();

    private user?: any;
    private slides: TutorialSlide[] = [];
    currentTutorialSection: TutorialSection | null = null;

    private routerEventsSubscription!: Subscription;

    private isSettingsSlides: boolean = false;
    private forceAdminTutorial: boolean = false;
    private seenSlides: TutorialSlide[] = [];

    //tabIndexMap store the relationship between the tutorial sections and their corresponding tab indices in the UI
    private tabIndexMap: Record<TutorialSection, number | null> = {
        [TutorialSection.PROFILE_SETTINGS]: 0,
        [TutorialSection.NOTE_SETTINGS]: 1,
        [TutorialSection.SCHEDULE_SETTINGS]: 2,
        [TutorialSection.CONSENT_SETTINGS]: 3,
        [TutorialSection.CODING_SETTINGS]: 5,
        [TutorialSection.NETWORK_SETTINGS]: 7,
        [TutorialSection.CLOSING_SLIDE]: null, //Need to add the closing slide here
        [TutorialSection.SCHEDULE_NAV]: null,
        [TutorialSection.PATIENT_NAV]: null,
        [TutorialSection.SUPPORT_STAFF]: null,
        [TutorialSection.DIALOG_SLIDE]: null,
        [TutorialSection.DOCTOR_OPTIONS_SLIDE]: null,
        [TutorialSection.GETTING_STARTED]: null,
        [TutorialSection.OLD_USER_LOGIN]: null,
        [TutorialSection.DOCTOR_FORCE_SCHEDULE_NAV_TUTORIAL]: null,
    };

    private dataLoaded = new BehaviorSubject(false);
    private requiredData: {
        user: any,
        group: any,
        groupUsers: any,
        hospitals: any,
        medicationBundles: any,
        timeSettings: any,
        appointmentTypes: any,
        defaultConsentForm: any,
        customConsentForm: any,
        colleagues: any,
        codingBundles: any,
        noteTypes: any,
        hiddenSlides: any
    } = {
            user: undefined,
            group: undefined,
            groupUsers: undefined,
            hospitals: undefined,
            medicationBundles: undefined,
            timeSettings: undefined,
            appointmentTypes: undefined,
            defaultConsentForm: undefined,
            customConsentForm: undefined,
            colleagues: undefined,
            codingBundles: undefined,
            noteTypes: undefined,
            hiddenSlides: undefined
        };

    constructor(
        private http: HttpClient,
        private userService: UserService,
        private groupService: GroupService,
        private hospitalService: HospitalService,
        private medicationService: MedicationService,
        private scheduleService: ScheduleService,
        private appointmentTypeService: AppointmentTypeService,
        private consentFormService: ConsentFormsService,
        private colleagueService: ColleagueService,
        private codingBundleService: CodingBundleService,
        private noteTypeService: NoteTypeService,
        private dialogRef: MatDialog,
        private router: Router,
        private feedbackService: FeedbackService

    ) {

        this.userService.userProfile$
            .subscribe(user => { this.user = user; this.requiredData.user = user; this.fetchHiddenSlides(); this.checkDataLoaded() });

        this.groupService.selectedGroup$
            .subscribe(group => { this.requiredData.group = group; this.checkDataLoaded() });

        this.hospitalService.hospitals$
            .subscribe(hospitals => { this.requiredData.hospitals = hospitals; this.checkDataLoaded() });

        this.groupService.groupUsers$
            .subscribe(users => { this.requiredData.groupUsers = users; this.checkDataLoaded() });

        this.medicationService.bundles$
            .subscribe(bundles => { this.requiredData.medicationBundles = bundles; this.checkDataLoaded() });

        this.scheduleService.schedule$
            .subscribe(schedule => {
                if (schedule) {
                    const { timeSlotDuration, startTime, endTime } = schedule;
                    this.requiredData.timeSettings = { timeSlotDuration, startTime, endTime }
                }
                this.checkDataLoaded();
            });

        this.appointmentTypeService.appointmentTypes$
            .subscribe(appointmentTypes => {
                if (appointmentTypes) {
                    this.requiredData.appointmentTypes = appointmentTypes;
                }
                this.checkDataLoaded();
            });

        this.consentFormService.consentForms$
            .subscribe(consentForms => {
                if (consentForms) {
                    this.requiredData.defaultConsentForm = consentForms.filter((c: any) => !c.canDelete);
                    this.requiredData.customConsentForm = consentForms.filter((c: any) => c.canDelete)
                }
                this.checkDataLoaded();
            });

        this.colleagueService.colleagues$
            .subscribe(colleagues => { this.requiredData.colleagues = colleagues; this.checkDataLoaded() });

        this.codingBundleService.codingBundles$
            .subscribe(codingBundles => { this.requiredData.codingBundles = codingBundles; this.checkDataLoaded() });

        this.noteTypeService.noteTypes$
            .subscribe(noteTypes => { this.requiredData.noteTypes = noteTypes; this.checkDataLoaded() });

        this.subscribeToRouteChanges();

    }

    // ngOnInit() {
    //     this.router.events
    //         .pipe(
    //             filter(event => event instanceof NavigationEnd) // Listen only for NavigationEnd events
    //         )
    //         .subscribe(() => {
    //             // Navigation is complete, now start the tutorial
    //             this.startTutorial(TutorialSection.PATIENT_NAV);
    //         });
    // }

    private checkDataLoaded() {
        if (this.dataLoaded.getValue()) return;
        if (!this.requiredData.user) return;
        if (!this.requiredData.group) return;
        if (!this.requiredData.hospitals) return;
        if (!this.requiredData.groupUsers) return;
        if (!this.requiredData.medicationBundles) return;
        if (!this.requiredData.timeSettings) return;
        if (!this.requiredData.appointmentTypes) return;
        if (!this.requiredData.defaultConsentForm) return;
        if (!this.requiredData.colleagues) return;
        if (!this.requiredData.codingBundles) return;
        if (!this.requiredData.noteTypes) return;
        this.dataLoaded.next(true);
    }

    //private previousUrl: string | null = null;

    private subscribeToRouteChanges() {
        // console.log("Subscribed to route changes", this.requiredData.user, this.requiredData.group);
        this.router.events.subscribe((event: any) => {
            //console.log("Route changed to:", event.urlAfterRedirects, typeof event);
            if (event instanceof NavigationEnd) {
                this.seenSlides = [];
                this.isSettingsSlides = false;
                switch (event.urlAfterRedirects) {
                    case '/settings':
                        this.isSettingsSlides = true;
                        break;
                    // Auto-trigger disabled: Tutorials should only start manually via help dialog
                    // case '/patients/schedule':
                    //     this.startTutorial(TutorialSection.SCHEDULE_NAV);
                    //     break;
                    // case '/patients/list':
                    //     if (this.requiredData.user?.userType && this.requiredData.user?.userType !== 'doctor') {
                    //         // Start tutorial for all non-doctor user type
                    //         this.startTutorial(TutorialSection.PATIENT_NAV);
                    //     }
                    //     else if (this.forceAdminTutorial) {
                    //         // Start tutorial for doctors only if they came from settings
                    //         this.startTutorial(TutorialSection.PATIENT_NAV);
                    //     }
                    //     break;
                }
                // Update previous URL after handling the current route change
                //this.previousUrl = event.urlAfterRedirects;
            }
        });
    }


    setSlide(slide: TutorialSlide) {
        if (slide.position) {
            slide.position = this.calculateResponsivePosition(slide.position);
        }
        this.currentSlideSubject.next(slide);
        this.seenSlides.push(slide);
    }


    private calculateResponsivePosition(position: TutorialPosition): TutorialPosition {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        // Adjust the x and y coordinates proportionally to the screen size
        const adjustedX = position.x ? (position.x / 1920) * screenWidth : undefined; // Assuming 1920px is the base width
        const adjustedY = position.y ? (position.y / 1080) * screenHeight : undefined; // Assuming 1080px is the base height

        return {
            ...position,
            x: adjustedX,
            y: adjustedY,
        };
    }

    registerExpansionTarget(id: string, panel: MatExpansionPanel): void {
        this.expansionTargets.set(id, panel);
    }

    getExpansionTarget(id: string): MatExpansionPanel | undefined {
        return this.expansionTargets.get(id);
    }

    registerHighlightTarget(id: string, panel: ElementRef<HTMLElement>): void {
        this.highlightTargets.set(id, panel);
    }

    getHighlightTarget(id: string): ElementRef<HTMLElement> | undefined {
        return this.highlightTargets.get(id);
    }

    registerFormTarget(id: string, panel: ElementRef<HTMLElement>): void {
        this.formTargets.set(id, panel);
    }

    getFormTarget(id: string): ElementRef<HTMLElement> | undefined {
        return this.formTargets.get(id);
    }


    async handleAction(actionName: SlideAction) { //Handle the start of new tutorial
        switch (actionName) {
            case SlideAction.SHOW_NEXT_SLIDE:
                this.showNextSlide();
                break;
            case SlideAction.CLOSE_TUTORIAL:
                this.closeTutorial();
                break;
            case SlideAction.START_NETWORK_TUTORIAL:
                this.startTutorial(TutorialSection.NETWORK_SETTINGS);
                break;
            case SlideAction.START_CODING_TUTORIAL:
                this.startTutorial(TutorialSection.CODING_SETTINGS);
                break;
            case SlideAction.START_SCHEDULE_TUTORIAL:
                this.startTutorial(TutorialSection.SCHEDULE_SETTINGS);
                break;
            case SlideAction.START_NOTE_TUTORIAL:
                this.startTutorial(TutorialSection.NOTE_SETTINGS);
                break;
            case SlideAction.START_CONSENT_TUTORIAL:
                this.startTutorial(TutorialSection.CONSENT_SETTINGS);
                break;
            case SlideAction.START_SCHEDULE_SIDE_NAV_TUTORIAL:
                this.startTutorial(TutorialSection.SCHEDULE_NAV);
                break;
            case SlideAction.START_PATIENT_SIDE_NAV_TUTORIAL:
                this.forceAdminTutorial = true;
                this.router.navigate(['/patients/list', {
                    outlets: { patient: null }
                }]);
                setTimeout(() => {
                    this.startTutorial(TutorialSection.PATIENT_NAV)
                })
                break;
            case SlideAction.NAVIGATE_SCHEDULE:
                this.closeTutorial();
                this.router.navigate(['/patients/schedule', {
                    outlets: {
                        patient: null,
                    }
                }]);
                this.updateUserTutorialStatus(UserTutorialStatusType.OLD_USER, true, false);
                break;
            case SlideAction.SHOW_CLOSING_SLIDE:
                this.startTutorial(TutorialSection.CLOSING_SLIDE);
                break;
            case SlideAction.SHOW_NOW:
                //Show if user is physician else
                if (this.requiredData.user?.userType === 'doctor') {
                    const sub = this.setTutorialCompleted().subscribe({
                        next: () => {
                            sub.unsubscribe();
                        },
                        error: (err: Error) => {
                            sub.unsubscribe();
                        }
                    });
                    // this.closeTutorial();
                    this.router.navigate(['/patients/schedule', {
                        outlets: {
                            patient: null,
                        }
                    }]);
                    setTimeout(() => {
                        if (!this.requiredData.user?.tutorialStatus?.allSeen) {
                            this.startTutorial(TutorialSection.DOCTOR_OPTIONS_SLIDE, true);
                            this.updateUserTutorialStatus(UserTutorialStatusType.OLD_USER, true, false);
                        }
                    }, 0)
                } else {
                    this.handleAction(SlideAction.NAVIGATE_SCHEDULE)
                }

                break;
            case SlideAction.OLD_LOGIN_CONFIRM_NOW:
                this.updateUserTutorialStatus(UserTutorialStatusType.OLD_USER, true);
                this.startTutorial(TutorialSection.GETTING_STARTED);
                break;
            case SlideAction.START_DOCTOR_FORCE_SCHEDULE_SIDE_NAV_TUTORIAL:
                this.forceAdminTutorial = true;
                this.updateUserTutorialStatus(UserTutorialStatusType.OLD_USER, true, false);
                this.startTutorial(TutorialSection.DOCTOR_FORCE_SCHEDULE_NAV_TUTORIAL);
                break;
            case SlideAction.OLD_LOGIN_CONFIRM_NEVER:
                this.forceZIndex.next(999);
                const confirmed = await this.feedbackService.showConfirmation('We Get It', 'You can restart this tutorial at any time by visiting the help menu and pressing the "Restart Tutorial" button', undefined, 'Cancel', 'Ok')
                this.forceZIndex.next(99999);
                if (confirmed) {
                    this.updateUserTutorialStatus(UserTutorialStatusType.ALL, true, false);
                    this.closeTutorial();
                }
                break;
            case SlideAction.CLOSE_GETTING_STARTED:
                if (this.requiredData.user?.userType === 'doctor' || this.requiredData.group?.userRole === 'Admin') {
                    // this.closeTutorial();
                    this.router.navigate(['/settings', {
                        outlets: {
                            patient: null,
                        }
                    }]);
                } else {
                    this.handleAction(SlideAction.NAVIGATE_SCHEDULE);
                    this.startTutorial(TutorialSection.SCHEDULE_NAV);
                }
                break;
        }
    }

    // Fetch Hidden Slides
    fetchHiddenSlides(): void {
        if (!this.user) return;
        const token = localStorage.getItem('authToken');
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        this.http.get<string[]>(`${environment.API_URL}/api/user/hidden-slides`, { headers }).subscribe({
            next: (hiddenSlides: any) => {
                //console.log('Fetched hidden slides from the server:', hiddenSlides);
                if (typeof hiddenSlides == 'string') {
                    hiddenSlides = JSON.parse(hiddenSlides);
                }
                this.hiddenSlides = hiddenSlides || []; // Fallback to an empty array if null
                this.checkDataLoaded();
            },
            error: (err) => {
                console.error('Failed to fetch hidden slides:', err);
                this.hiddenSlides = []; // Fallback to an empty array on error
            },
        });
    }

    //Go To Previous Slide
    goToPrevSlide() {

    }

    // Hide Card Permanently
    hideCardPermanently(key: string): void {
        //console.log('hideCardPermanently called with key:', key);

        if (!this.hiddenSlides) {
            this.hiddenSlides = [];
        }

        if (this.hiddenSlides.includes(key)) {
            //console.log(`Slide with key "${key}" is already hidden.`);
            return;
        }

        //console.log('hidden slides before push: ', this.hiddenSlides);
        this.hiddenSlides.push(key);
        //console.log('Updated hiddenSlides:', this.hiddenSlides);

        const token = localStorage.getItem('authToken'); // Replace with your token storage mechanism
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        this.http.post(`${environment.API_URL}/api/user/hidden-slides`, { hiddenSlides: this.hiddenSlides }, { headers }).subscribe({
            next: () => {
                console.log(`Slide with key "${key}" is now hidden permanently.`);
            },
            error: (err) => {
                console.error('Failed to update hidden slides:', err);
                this.hiddenSlides = this.hiddenSlides.filter((slide) => slide !== key);
            },
        });
        this.closeTutorial();
    }

    closeTutorial(): void {
        this.currentSlideSubject.next(null);
    }

    private loadedSub: Subscription | undefined;

    startTutorial(type: TutorialSection, isTabClick = false): void {
        this.currentTutorialSection = type; //Track current section

        if (!this.dataLoaded.value) {
            this.loadedSub?.unsubscribe();
            setTimeout(() => {
                this.loadedSub = this.dataLoaded.subscribe((loaded: boolean) => {
                    if (loaded) {
                        this.startTutorial(type, isTabClick);
                    }
                });
            }, 1000);
            return;
        }

        this.dialogRef.closeAll(); // Close any open dialogs

        switch (type) {
            case TutorialSection.PROFILE_SETTINGS:
                if (this.requiredData.user.userType === 'doctor' && this.requiredData.group.userRole === 'Physician') {
                    this.slides = DOCTOR_ONLY_PROFILE_SETTINGS_SLIDES;
                } else {
                    this.slides = PROFILE_SETTINGS_SLIDES;
                }
                break;
            case TutorialSection.NOTE_SETTINGS:
                this.slides = NOTE_SETTINGS_SLIDES;
                break;
            case TutorialSection.SCHEDULE_SETTINGS:
                this.slides = SCHEDULE_SETTINGS_SLIDES;
                break;
            case TutorialSection.CONSENT_SETTINGS:
                this.slides = CONSENT_SETTINGS_SLIDES;
                break;
            case TutorialSection.CODING_SETTINGS:
                this.slides = CODING_SETTINGS_SLIDES;
                break;
            case TutorialSection.NETWORK_SETTINGS:
                this.slides = NETWORK_SETTINGS_SLIDES;
                break;
            case TutorialSection.GETTING_STARTED:
                this.slides = GETTING_STARTED;
                break;
            case TutorialSection.SCHEDULE_NAV:
                if (moment(this.requiredData.user.createdAt).isSameOrBefore(moment('2025-02-08', 'YYYY-MM-DD')) && !this.requiredData.user?.tutorialStatus?.allSeen && !this.requiredData.user?.tutorialStatus?.oldUserSeen && this.requiredData.user.userType === 'doctor') {
                    this.slides = OLD_USER_LOGIN
                }
                else if (!this.requiredData.user?.tutorialStatus?.introductionSeen) {
                    this.slides = GETTING_STARTED;
                }
                else if (this.requiredData.user.userType !== 'doctor') {
                    this.slides = SCHEDULE_SLIDE_NAV;
                } else {
                    this.slides = [];
                }
                break;
            case TutorialSection.DOCTOR_OPTIONS_SLIDE:
                this.slides = DOCTER_OPTION_SLIDES;
                break;
            case TutorialSection.DOCTOR_FORCE_SCHEDULE_NAV_TUTORIAL:
                this.slides = SCHEDULE_SLIDE_NAV;
                break;
            case TutorialSection.PATIENT_NAV:
                if (!this.requiredData.user?.tutorialStatus?.invitingSeen) {
                    this.slides = PATIENT_SLIDE_NAV;
                }
                else if (this.requiredData.user?.userType !== 'doctor') {
                    this.slides = PATIENT_SLIDE_NAV;
                }
                //console.log('GOT PATIENT TUT SLIDES BECAUSE OF SWITCH MATCH')
                break;
            case TutorialSection.CLOSING_SLIDE:
                this.slides = CLOSING_SLIDE
                break;
        }

        //console.log('STARTING TUTORIAL: ', type);
        //console.log('GOT TUTORIAL SLIDES: ', this.slides);

        if (isTabClick) {
            let nextIndex = 0;
            while (
                nextIndex < this.slides.length &&
                evaluateHideConditions(
                    this.slides[nextIndex].hideConditions || [],
                    //this,
                    this.slides[nextIndex],
                    this.router,
                    this.requiredData
                )
            ) {
                nextIndex++;
            }

            if (nextIndex < this.slides.length) {
                this.showNextSlide(nextIndex)

            } else {
                //console.warn('No available slides to show. Tutorial may end.');
                this.closeTutorial();
            }
        } else {

            // Check and auto-redirect to the nearest valid section with slides
            let nextIndex = 0;
            while (
                nextIndex < this.slides.length &&
                evaluateHideConditions(
                    this.slides[nextIndex].hideConditions || [],
                    // this,
                    this.slides[nextIndex],
                    this.router,
                    this.requiredData
                )
            ) {
                nextIndex++;
            }

            if (nextIndex < this.slides.length) {
                //Emit the corresponding tab index
                //console.log('SLIDE CURRENT TAB: ', nextIndex)
                const tabIndex = this.tabIndexMap[type];
                /* if (tabIndex !== undefined) {
                    this.tabChangeSubject.next(tabIndex);
                    this.showNextSlide(nextIndex);
                } */
                if (tabIndex !== undefined && tabIndex !== null) {
                    this.tabChangeSubject.next(tabIndex);
                }
                this.showNextSlide(nextIndex);

            } else {
                //console.warn('No available slides in the current section. Searching for the next section with slides...');
                const tabs = Object.entries(this.tabIndexMap); // All tab mappings [TutorialSection, tabIndex]
                const currentTabIndex = tabs.findIndex(([key]) => key === type); // Current tab index
                let foundSlides = false;

                // Iterate over the following tabs to find the next one with valid slides
                for (let i = currentTabIndex + 1; i < tabs.length; i++) {
                    const [nextType, nextTabIndex] = tabs[i];
                    const nextSlides = this.mapSectionToSlides(nextType as TutorialSection).filter((slide) =>
                        !evaluateHideConditions(slide.hideConditions || [], slide, this.router, this.requiredData)
                    );
                    //console.log('GOT NEXT SLIDES: ', nextSlides);
                    //console.log(nextType, nextTabIndex)
                    if (nextSlides.length > 0 && nextTabIndex !== null) {
                        //console.log(`Found slides in section: ${nextType}`);

                        // Ensure the tabChangeSubject emits and triggers tab redirection
                        this.tabChangeSubject.next(nextTabIndex);

                        // Delay slide rendering to ensure tab switch happens first
                        setTimeout(() => {
                            this.slides = nextSlides;
                            this.tabChangeSubject.next(nextTabIndex);
                            this.showNextSlide(0); // Show the first available slide
                        }, 1000); // Small delay to allow UI state to update

                        foundSlides = true;
                        break;
                    }
                }

                if (!foundSlides && this.seenSlides.length !== 0 && this.isSettingsSlides) {
                    //console.warn('No available slides in subsequent sections. Tutorial will end.');
                    // this.closeTutorial();
                    this.startTutorial(TutorialSection.CLOSING_SLIDE, false)
                }
            }

        }
    }

    private mapSectionToSlides(type: TutorialSection): TutorialSlide[] {
        switch (type) {
            case TutorialSection.PROFILE_SETTINGS:
                return PROFILE_SETTINGS_SLIDES;
            case TutorialSection.NOTE_SETTINGS:
                return NOTE_SETTINGS_SLIDES;
            case TutorialSection.SCHEDULE_SETTINGS:
                return SCHEDULE_SETTINGS_SLIDES;
            case TutorialSection.CONSENT_SETTINGS:
                return CONSENT_SETTINGS_SLIDES;
            case TutorialSection.CODING_SETTINGS:
                return CODING_SETTINGS_SLIDES;
            case TutorialSection.NETWORK_SETTINGS:
                return NETWORK_SETTINGS_SLIDES;
            case TutorialSection.SCHEDULE_NAV:
                return SCHEDULE_SLIDE_NAV
            case TutorialSection.PATIENT_NAV:
                return PATIENT_SLIDE_NAV;
            case TutorialSection.CLOSING_SLIDE:
                return CLOSING_SLIDE;

            default:
                return [];
        }
    }

    showNextSlide(forceIndex?: number): void {
        let nextIndex = forceIndex !== undefined ? forceIndex : this.currentSlideIndex + 1;
        this.currentSlideIndex = nextIndex - 1;

        //console.log('SHOW NEXT SLIDE: ', nextIndex);
        while (nextIndex < this.slides.length && evaluateHideConditions(this.slides[nextIndex].hideConditions || [], this.slides[nextIndex], this.router, this.requiredData)
        ) {
            nextIndex++;
        }

        if (nextIndex < this.slides.length) {
            this.currentSlideIndex = nextIndex;
            const currentSlide = this.slides[this.currentSlideIndex];
            if (!currentSlide.keepDialogOpen) {
                //close any dialogs that are open before moving to the next slide
                this.dialogRef.closeAll();
            }
            this.currentSlideSubject.next(currentSlide);
            this.seenSlides.push(currentSlide);
        } else {
            this.closeTutorial();
        }
    }

    showPreviousSlide() {
        let previousIndex = this.currentSlideIndex - 1;

        //Check if the previous slide should be hidden
        while (
            previousIndex >= 0 &&
            evaluateHideConditions(
                this.slides[previousIndex].hideConditions || [],
                //this,
                this.slides[previousIndex],
                this.router,
                this.requiredData
            )
        ) {
            previousIndex--;
        }

        if (previousIndex >= 0) {
            //Show the previos slide
            this.showNextSlide(previousIndex) //Re-use the existing to show the slide since the functionality is the same
        } else {
            console.warn('No Previous slides in the current section. Searching for the previous section with slides...');

            //Find the previous section with valid slides
            const tabs = Object.entries(this.tabIndexMap) //All tab mappings [TutorialSection, tabIndex]
            //const currentTabIndex = tabs.findIndex(([key]) => this.slides === this.mapSectionToSlides(key as TutorialSection)) // Current tab index
            const currentTabIndex = tabs.findIndex(([key]) => key === this.currentTutorialSection); // Current tab index
            let foundSlides = false;

            //Iterate over the previous tabs to find the next one with valid slides
            for (let i = this.currentSlideIndex - 1; i >= 0; i--) {
                const [previousType, previousTabIndex] = tabs[i];
                const previousSlides = this.mapSectionToSlides(previousType as TutorialSection).filter((slide) => !evaluateHideConditions(slide.hideConditions || [], slide, this.router, this.requiredData,));
                //console.log('GOT PREVIOUS SLIDES: ', previousSlides);
                if (previousSlides.length >= 0) {
                    //console.log(`Found slided in section: ${previousType}`)

                    //Ensure the tabChangeSubject emits and triggers tab redirection
                    this.tabChangeSubject.next(previousTabIndex);

                    //Delay slides rendering to ensure tab switch happens first
                    setTimeout(() => {
                        this.slides = previousSlides;
                        //this.tabChangeSubject.next(previousTabIndex);
                        this.showNextSlide(previousSlides.length - 1); //Show the last slide of the previous section
                    }, 500)//Small delay to allow UI state to change/update

                    foundSlides = true;
                    break;
                }
            }

            if (!foundSlides) {
                //console.warn('No available slides in previos sections. Tutorial will end.');
                this.closeTutorial()
            }
        }

    }

    resetTutorialToDefaults(): Observable<any> {
        const userId = this.requiredData.user?.id;
        this.requiredData.user.forceTutorial = true;

        const token = localStorage.getItem('authToken');
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.post(`${environment.API_URL}/api/user/tutorial/reset/${userId}`, {}, { headers }).pipe(
            tap((result: any) => {
                this.fetchHiddenSlides();
            }),
            catchError((error) => {
                console.error('Error resetting tutorial progress:', error);
                return throwError(() => error);
            })
        );
    }

    setTutorialCompleted(): Observable<any> {
        const userId = this.requiredData.user?.id;
        this.requiredData.user.forceTutorial = false;
        const token = localStorage.getItem('authToken');
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
        return this.http.post(`${environment.API_URL}/api/user/tutorial/completed/${userId}`, {}, { headers }).pipe(
            catchError((error) => {
                console.error('Error setting tutorial completed:', error);
                return throwError(() => error);
            })
        );
    }

    updateUserTutorialStatus(type: UserTutorialStatusType, seen: boolean, forceTutorial?: boolean): void {

        let currentStatus: UserTutorialStatus = this.requiredData.user.tutorialStatus || {};

        switch (type) {
            case UserTutorialStatusType.INTRODUCTION:
                currentStatus.introductionSeen = seen;
                break;
            case UserTutorialStatusType.INVITING:
                currentStatus.invitingSeen = seen;
                break;
            case UserTutorialStatusType.SCHEDULING:
                currentStatus.schedulingSeen = seen;
                break;
            case UserTutorialStatusType.DISCHARGING:
                currentStatus.dischargingSeen = seen;
                break;
            case UserTutorialStatusType.OLD_USER:
                currentStatus.oldUserSeen = seen;
                break;
            case UserTutorialStatusType.ALL:
                currentStatus.allSeen = seen;
                break;
        }

        this.requiredData.user.tutorialStatus = currentStatus;
        if (forceTutorial !== undefined) {
            this.requiredData.user.forceTutorial = forceTutorial;
        }
        const userId = this.requiredData.user.id;
        const token = localStorage.getItem('authToken');
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
        let sub: Subscription = this.http.patch(`${environment.API_URL}/api/user/tutorial/status/${userId}`, { tutorialStatus: currentStatus, forceTutorial }, { headers })
            .pipe(
                catchError((error) => {
                    console.error('Error setting tutorial completed:', error);
                    return throwError(() => error);
                })
            ).subscribe(res => sub?.unsubscribe());
    }

    ngOnDestroy() {
        // Unsubscribe to prevent memory leaks
        this.routerEventsSubscription?.unsubscribe();
    }
    //     updateUserProgress(progress: number): void {
    //         if (!this.user) return

    //         this.user.progressBar = progress; //Update locally first then persist it

    //         const token = localStorage.getItem('authToken');
    //         const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    //         //Send update request to backend
    //         console.log('USER LOGGED ID: ', this.user?.id);

    //         this.http.put(`${environment.API_URL}/api/user/${this.user?.id}/progress`, { progress }, { headers })
    //             .pipe(
    //                 catchError((error) => {
    //                     console.error('Failed to update progress:', error);
    //                     return throwError(() => error);
    //                 })
    //             ).subscribe({
    //                 next: () => console.log('Progress updated successfully'),
    //                 error: (err) => console.error('Error updating progress:', err)
    //             });
    //     }
}
